import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { MultiSelect } from "../../../../../../BP2Components";
import { Icon_Search } from "../../../../../../BP2Components/Icons/icons";
import { useChat } from "../../../../../../../context/ChatContext/ChatContext";
import { useDispatch, useSelector, DefaultRootState } from "react-redux";
import { setChatSettings } from "../../../../../../../appRedux/actions/ChatSettings";
import { createNewRoom, findExistingRoom, generateRoomTitle } from "../../../ChatScripts";
import { v4 as uuidv4 } from "uuid";
import { useDebouncedCallback } from "use-debounce";
import VirtualizedMenuList from "./VirtualizedMenuList";

const MAX_OPTIONS = 200;
const DEBOUNCE_MS = 150;

const SearchPanel = () => {
   const [isMenuOpen, setIsMenuOpen] = useState(false);
   const [inputValue, setInputValue] = useState("");
   const [debouncedInput, setDebouncedInput] = useState("");

   const chatSettings = useSelector((state: DefaultRootState) => state.chatSettings);
   const { firstName, oldId } = useSelector((state: DefaultRootState) => state.user);

   const { chatData } = useChat();

   const intl = useIntl();

   const dispatch = useDispatch();

   const setDebouncedInputDelayed = useDebouncedCallback(setDebouncedInput, DEBOUNCE_MS);

   const handleInputChange = (value: string) => {
      setInputValue(value);
      setDebouncedInputDelayed(value);
   };

   const allOptions = useMemo(() => {
      const list = [...(chatData?.allRooms ?? []), ...(chatData?.phpUsers ?? [])];
      return list.map((item) => ({
         value: item,
         label: item.hasOwnProperty("title")
            ? item.title
            : `${item.firstName || ""} ${item.lastName || ""}`.trim(),
      }));
   }, [chatData?.allRooms, chatData?.phpUsers]);

   const filteredOptions = useMemo(() => {
      if (!debouncedInput) return allOptions;
      const lower = debouncedInput.toLowerCase();
      const results: typeof allOptions = [];
      for (const opt of allOptions) {
         if (opt.label.toString().toLowerCase().includes(lower)) {
            results.push(opt);
            if (results.length >= MAX_OPTIONS) break;
         }
      }
      return results;
   }, [allOptions, debouncedInput]);

   const handleOptionChange = (option) => {
      if (!option) return;

      // if user choose group room, we dispatching that room
      if (option?.value?.hasOwnProperty("title")) {
         dispatch(setChatSettings({ ...chatSettings, currentRoom: option?.value }));
      } else {
         // if user select other user:
         const currentUserId = oldId;
         const targetUserId = option?.value?.oldId;
         const targetUserName = option?.value?.firstName;

         // first we are checking if room between those users exist
         const existingRoom = findExistingRoom(chatData?.directRooms, currentUserId, targetUserId);

         if (existingRoom) {
            // if so, we are dispatching it
            dispatch(
               setChatSettings({
                  ...chatSettings,
                  currentRoom: existingRoom,
               }),
            );
         } else {
            // if room doesn`t exist, we are creating a new one
            const roomId = uuidv4();
            if (roomId && currentUserId && targetUserId && firstName && targetUserName) {
               createNewRoom(
                  roomId,
                  "direct",
                  generateRoomTitle(firstName, targetUserName),
                  [oldId, targetUserId],
                  currentUserId,
                  firstName,
                  targetUserName,
               );
               dispatch(
                  setChatSettings({
                     ...chatSettings,
                     currentRoom: {
                        id: roomId,
                        title: generateRoomTitle(firstName, targetUserName),
                        type: "direct",
                        status: "active",
                        seenBy: [oldId],
                        chatUsers: [oldId, targetUserId],
                     },
                  }),
               );
            }
         }
      }
   };

   return (
      <div className={`chat-searchbar ${isMenuOpen ? "menu-open" : ""}`}>
         <MultiSelect
            placeholder={intl.formatMessage({ id: "chat.search" })}
            className="chat-searchbar__searchbar-input"
            onChange={handleOptionChange}
            name=""
            noOptionsMessage="nie ma nic"
            closeMenuOnSelect={true}
            value={null}
            size="small"
            hideArrow={true}
            isSearchable
            isAsync={false}
            options={filteredOptions}
            isCreatable={false}
            isMulti={false}
            isClearable={true}
            onMenuOpen={() => setIsMenuOpen(true)}
            onMenuClose={() => setIsMenuOpen(false)}
            style={{ width: "223px" }}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            components={{ MenuList: VirtualizedMenuList }}
         />
         <Icon_Search fill="none" stroke="#94A3B8" className="chat-searchbar__icon" />
      </div>
   );
};

export default SearchPanel;
